import {
  ChangeDetectionStrategy,
  Component,
  booleanAttribute,
  input,
  output,
  signal,
} from '@angular/core';

export type VhAlertType = 'success' | 'warning' | 'error' | 'info';

/**
 * Inline alert banner. Non-blocking. Use `VhToastService` for transient
 * notifications.
 */
@Component({
  selector: 'vh-alert',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (visible()) {
      <div class="vh-alert vh-alert--{{ type() }}" role="alert">
        <span class="vh-alert__icon" aria-hidden="true">{{ icon() }}</span>
        <div class="vh-alert__body">
          <ng-content />
        </div>
        @if (dismissible()) {
          <button type="button" class="vh-alert__close" aria-label="Dismiss" (click)="dismiss()">
            &times;
          </button>
        }
      </div>
    }
  `,
  styleUrl: './alert.component.scss',
})
export class VhAlertComponent {
  readonly type = input<VhAlertType>('info');
  readonly dismissible = input(false, { transform: booleanAttribute });
  readonly dismissed = output<void>();

  private readonly _visible = signal(true);
  readonly visible = this._visible.asReadonly();

  icon(): string {
    switch (this.type()) {
      case 'success':
        return '✓';
      case 'warning':
        return '⚠';
      case 'error':
        return '!';
      case 'info':
      default:
        return 'ℹ';
    }
  }

  dismiss(): void {
    this._visible.set(false);
    this.dismissed.emit();
  }
}
